import React from "react";
import SkeletonBlock from "./SkeletonBlock";

const HOUR_LABELS = ["6 AM", "7 AM", "8 AM", "9 AM", "10 AM", "11 AM", "12 PM", "1 PM", "2 PM", "3 PM", "4 PM", "5 PM", "6 PM", "7 PM", "8 PM", "9 PM"];

const CalendarViewSkeleton: React.FC = () => (
  <div
    role="status"
    aria-label="Loading calendar"
    className="flex h-[calc(100vh-64px)] overflow-hidden"
  >
    {/* Main calendar area */}
    <div className="flex-1 min-w-0 p-2 flex flex-col">
      {/* Toolbar: prev/next buttons + title + view buttons */}
      <div className="flex items-center justify-between px-2 py-2 mb-2">
        <div className="flex items-center gap-1">
          <SkeletonBlock className="h-8 w-16 rounded-lg" />
          <SkeletonBlock className="h-8 w-8 rounded-lg" />
          <SkeletonBlock className="h-8 w-8 rounded-lg" />
        </div>
        <SkeletonBlock className="h-5 w-36" />
        <div className="flex items-center gap-1">
          <SkeletonBlock className="h-8 w-14 rounded-lg" />
          <SkeletonBlock className="h-8 w-14 rounded-lg" />
        </div>
      </div>

      {/* Calendar grid */}
      <div className="flex-1 border border-warm-border rounded-lg overflow-hidden">
        {/* Day column headers */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] bg-warm-bg-elevated border-b border-warm-border">
          <div className="py-2" />
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div
              key={day}
              className="py-2 text-center border-l border-warm-border"
            >
              <SkeletonBlock className="h-3 w-8 mx-auto mb-1" />
              <SkeletonBlock className="h-5 w-5 mx-auto rounded-full" />
            </div>
          ))}
        </div>

        {/* Time grid rows */}
        <div className="relative overflow-y-auto flex-1">
          {HOUR_LABELS.map((label) => (
            <div
              key={label}
              className="grid grid-cols-[60px_repeat(7,1fr)] h-12 border-b border-warm-border-subtle"
            >
              <div className="text-[10px] text-warm-text-tertiary pr-2 text-right pt-0.5 select-none">
                {label}
              </div>
              {Array.from({ length: 7 }, (_, i) => (
                <div key={i} className="border-l border-warm-border-subtle" />
              ))}
            </div>
          ))}

          {/* Ghost event blocks positioned over the grid */}
          <div className="absolute inset-0 grid grid-cols-[60px_repeat(7,1fr)] pointer-events-none">
            <div />
            {/* Mon */}
            <div className="relative border-l border-transparent">
              <SkeletonBlock className="absolute left-1 right-1 rounded" style={{ top: "96px", height: "48px" }} />
            </div>
            {/* Tue */}
            <div className="relative border-l border-transparent" />
            {/* Wed */}
            <div className="relative border-l border-transparent">
              <SkeletonBlock className="absolute left-1 right-1 rounded" style={{ top: "48px", height: "72px" }} />
              <SkeletonBlock className="absolute left-1 right-1 rounded" style={{ top: "168px", height: "48px" }} />
            </div>
            {/* Thu */}
            <div className="relative border-l border-transparent" />
            {/* Fri */}
            <div className="relative border-l border-transparent">
              <SkeletonBlock className="absolute left-1 right-1 rounded" style={{ top: "144px", height: "96px" }} />
            </div>
            {/* Sat */}
            <div className="relative border-l border-transparent" />
            {/* Sun */}
            <div className="relative border-l border-transparent" />
          </div>
        </div>
      </div>
    </div>

    {/* Unscheduled panel skeleton */}
    <div className="w-[280px] shrink-0 flex flex-col bg-warm-bg-elevated border-l border-warm-border h-full">
      <div className="px-4 py-3 border-b border-warm-border">
        <SkeletonBlock className="h-5 w-32" />
      </div>
      <div className="p-2 space-y-1.5">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-2">
            <SkeletonBlock className="w-1 h-6 rounded-full flex-shrink-0" />
            <SkeletonBlock className="h-4 flex-1" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default CalendarViewSkeleton;
